import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { rpcClient } from 'src/api/rpc/client';
import { useCan } from 'src/hooks/useCan';
import type { ListTasksInput } from '@erp/rpc';

/** Queue poll cadence while tasks are running, and otherwise. */
export const TASK_QUEUE_ACTIVE_POLL_INTERVAL_MS = 5000;
export const TASK_QUEUE_POLL_INTERVAL_MS = 30_000;

/**
 * Server-filtered, page-based task list for the `/tasks` queue page. The page
 * translates its URL search params into `ListTasksInput` filters; this hook is
 * a thin pass-through to `tasks.list` with previous-page data kept during
 * refetches so paging doesn't blank the table.
 */
export function useTaskQueue(input: ListTasksInput, enabled = true) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const query = useQuery({
    queryKey: ['task-queue', input],
    queryFn: () => rpcClient.tasks.list(input),
    enabled: canReadTasks && enabled,
    placeholderData: keepPreviousData,
    // Poll while work is live so status changes surface without a manual
    // refresh, matching the inbox tasks tab's cadence.
    refetchInterval: (q) => {
      const tasks = q.state.data?.tasks ?? [];
      const hasActive = tasks.some(
        (t) => t.status === 'in_progress' || t.status === 'pending',
      );
      return hasActive
        ? TASK_QUEUE_ACTIVE_POLL_INTERVAL_MS
        : TASK_QUEUE_POLL_INTERVAL_MS;
    },
  });

  return {
    tasks: query.data?.tasks ?? [],
    total: query.data?.pagination.total ?? 0,
    snoozedCount: query.data?.pagination.snoozedCount ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
  };
}
